/* STEP 7e-2 (owner "Step 7e Methodology Card V1", 30-Oct-2026 15:10 KSA) — EN + AR of the screens 7e-2 adds:
   s7e2.settings  the "Firm settings" dialog of a methodology version (S1);
   s7e2.range     the firm range / minimum / default next to the 105(a) % fields and the Sampling fields, and the reason (S2);
   s7e2.dev       the deviation panel on the File assembly page (S3);
   s7e2.report    the firm deviation report on the Methodology page (S3);
   errors.*       the new refusal codes (incl. the owner add-ons: change refused on a locked engagement, delete refused).
   The help panel "How the Methodology tab works" stays in 1860_s7e1_methodology.js (s7e1.help — one place). No provider names. */
export const I18N_S7E2 = {
  en: {
    s7e2: {
      settings: {
        button: "Firm settings",
        title: "Firm settings — V{n}",
        intro: "AZEX reads this version's rules and summary and suggests a value for each setting with its section and page. A suggestion is not used until a manager confirms that setting.",
        frozen: "This version is superseded: its firm settings are frozen and kept as history. They still apply to the engagements that use this version.",
        readOnly: "Only the Firm Admin, a Partner (including the title Director), a Senior Manager, an Audit Manager or a Manager can confirm or change these settings.",
        suggest: "Suggest from the methodology",
        suggestQueued: "Suggestions queued",
        running: "Reading the methodology…",
        lastRun: "Last suggestions: {at} · {found} of {total} found",
        aiRan: "The AI step was asked for the settings the rule text did not give: {n} found.",
        aiNotRun: "The AI step did not run ({why}); only the rule text was read.",
        jobFailed: "The last suggestion run failed: {err}",
        why: { no_key: "the firm has no AI key", exhausted: "the AI allowance is used up", unavailable: "the AI service could not be reached", nothing: "every setting was read from the rule text", no_lines: "no extracted rules or summary yet" },
        colSetting: "Setting",
        colSuggested: "Suggested from the methodology",
        colInUse: "In use",
        notFound: "Not found in this methodology",
        source: "§{s} · p. {p}",
        byRule: "read from the rule text",
        byAi: "AI",
        notConfirmed: "Not confirmed — not used",
        confirmedBy: "Confirmed by {by} · {at}",
        edited: "Edited (suggested: {v})",
        confirm: "Confirm",
        edit: "Edit",
        save: "Save",
        cancel: "Cancel",
        clear: "Clear",
        min: "Min %",
        max: "Max %",
        pct: "%",
        text: "Text",
        guidance: "Guidance for the team only — AZEX's sign-off and lock rules do not change.",
        group: { materiality: "Materiality", sampling: "Sampling", risk: "Risk assessment", review: "Review" },
        field: {
          mat_pbt: "Materiality % — profit before tax",
          mat_revenue: "Materiality % — revenue",
          mat_total_assets: "Materiality % — total assets",
          mat_equity: "Materiality % — equity",
          mat_expenses: "Materiality % — expenses",
          mat_other: "Materiality % — other benchmark",
          pm_pct: "Performance materiality % of overall materiality",
          ct_pct: "Clearly trivial % of overall materiality",
          sampling_min_confidence: "Sampling — minimum confidence level %",
          expected_misstatement_pct: "Sampling — default expected misstatement % of tolerable misstatement",
          significant_risk_criteria: "Significant-risk criteria",
          review_levels: "Review levels the firm requires",
        },
      },
      range: {
        firm: "Firm range {r}",
        firmMin: "Firm minimum {r}",
        firmDefault: "Firm default {r} of tolerable misstatement",
        version: "firm methodology V{n}",
        outside: "Outside the firm range — enter the reason (a deviation is recorded)",
        below: "Below the firm minimum — enter the reason (a deviation is recorded)",
        reasonPh: "Why this engagement uses a figure outside the firm's methodology",
        openDev: "Open deviation — reason on record: {r}",
      },
      dev: {
        title: "Methodology deviations",
        intro: "Each figure saved outside the firm's methodology settings. The engagement partner acknowledges each open deviation; until then the Partner sign-off and the lock are refused.",
        colItem: "Area",
        colSetting: "Setting",
        colFirm: "Firm setting",
        colValue: "Engagement value",
        colReason: "Reason",
        colBy: "Recorded by",
        colVersion: "Version",
        colStatus: "Status",
        colAck: "Acknowledgement",
        status: { open: "Open — not acknowledged", acknowledged: "Open — acknowledged", closed: "Closed" },
        area: { materiality: "Materiality", sampling: "Sampling" },
        ack: "Acknowledge",
        notePh: "Note (optional)",
        acked: "Acknowledged by {by} · {at}",
        closedAt: "Closed {at} (value {v})",
        onlyPartner: "Only the engagement partner acknowledges a deviation.",
        noPartner: "Appoint the engagement partner in Team first.",
        done: "Deviation acknowledged",
      },
      report: {
        title: "Firm deviation report",
        intro: "Every methodology deviation across the firm's engagements, with its status.",
        engagement: "Engagement",
        allEngagements: "All engagements",
        area: "Area",
        allAreas: "All areas",
        status: "Status",
        allStatuses: "All statuses",
        export: "Export XLSX",
        empty: "No deviations.",
        counts: "{open} open — not acknowledged · {acknowledged} open — acknowledged · {closed} closed",
      },
    },
    errors: {
      METHODOLOGY_SETTINGS_FORBIDDEN: "Only a Manager (Senior or Audit Manager), a Partner / Director or the Firm Admin may change the firm settings of a methodology version.",
      METHODOLOGY_SETTINGS_FROZEN: "This methodology version is superseded: its firm settings are frozen (kept as history).",
      METHODOLOGY_SETTING_VALUE_REQUIRED: "Enter the value to confirm — this methodology has no suggestion for it.",
      METHODOLOGY_SETTING_INVALID: "The value is not valid for this setting.",
      METHODOLOGY_DEVIATION_REASON_REQUIRED: "Enter the reason for using a figure outside the firm's methodology setting.",
      METHODOLOGY_DEVIATION_UNACKNOWLEDGED: "The Partner sign-off and the lock are refused: the engagement partner has not acknowledged the methodology deviations.",
      METHODOLOGY_DEVIATION_PARTNER_ONLY: "Only the engagement partner can acknowledge a methodology deviation.",
      METHODOLOGY_DEVIATION_CLOSED: "This deviation is closed: there is nothing to acknowledge.",
      METHODOLOGY_REPORT_FORBIDDEN: "Only a Manager (Senior or Audit Manager), a Partner / Director or the Firm Admin may open the firm deviation report.",
      METHODOLOGY_RELINK_LOCKED: "This engagement is signed off (locked): its methodology version cannot be changed. Unlock the engagement first.",
      METHODOLOGY_DELETE_CURRENT: "This is the current approved methodology version: it cannot be deleted. Approve a newer version first; this one then becomes superseded.",
      METHODOLOGY_DELETE_LINKED: "This methodology version is used by engagements: it cannot be deleted. Change those engagements to another version first.",
    },
  },
  ar: {
    s7e2: {
      settings: {
        button: "إعدادات المكتب",
        title: "إعدادات المكتب — ⁦V{n}⁩",
        intro: "يقرأ AZEX قواعد هذا الإصدار وملخصه ويقترح قيمة لكل إعداد مع القسم والصفحة. ولا يُستخدم أي اقتراح قبل أن يؤكد مدير ذلك الإعداد.",
        frozen: "هذا الإصدار مُستبدل: إعدادات المكتب الخاصة به مجمّدة ومحفوظة كسجل. وتظل سارية على المهام التي تستخدم هذا الإصدار.",
        readOnly: "لا يؤكد هذه الإعدادات أو يغيّرها إلا مسؤول الشركة أو الشريك (بما في ذلك لقب المدير التنفيذي) أو المدير الأول أو مدير المراجعة أو المدير.",
        suggest: "اقتراح من المنهجية",
        suggestQueued: "أُضيفت الاقتراحات إلى قائمة الانتظار",
        running: "جارٍ قراءة المنهجية…",
        lastRun: "آخر اقتراحات: {at} · وُجد {found} من {total}",
        aiRan: "طُلب من خطوة الذكاء الاصطناعي ما لم يذكره نص القواعد: وُجد {n}.",
        aiNotRun: "لم تعمل خطوة الذكاء الاصطناعي ({why})؛ قُرئ نص القواعد فقط.",
        jobFailed: "فشل آخر تشغيل للاقتراحات: {err}",
        why: { no_key: "لا يوجد مفتاح ذكاء اصطناعي للمكتب", exhausted: "استُنفد رصيد الذكاء الاصطناعي", unavailable: "تعذّر الوصول إلى خدمة الذكاء الاصطناعي", nothing: "قُرئت كل الإعدادات من نص القواعد", no_lines: "لا توجد قواعد مستخرجة أو ملخص بعد" },
        colSetting: "الإعداد",
        colSuggested: "مقترح من المنهجية",
        colInUse: "المستخدم",
        notFound: "غير موجود في هذه المنهجية",
        source: "§{s} · ص {p}",
        byRule: "مقروء من نص القاعدة",
        byAi: "الذكاء الاصطناعي",
        notConfirmed: "غير مؤكد — غير مستخدم",
        confirmedBy: "أكّده {by} · {at}",
        edited: "مُعدَّل (المقترح: {v})",
        confirm: "تأكيد",
        edit: "تعديل",
        save: "حفظ",
        cancel: "إلغاء",
        clear: "مسح",
        min: "الحد الأدنى %",
        max: "الحد الأعلى %",
        pct: "%",
        text: "نص",
        guidance: "إرشاد للفريق فقط — لا تتغير قواعد التوقيع والقفل في AZEX.",
        group: { materiality: "الأهمية النسبية", sampling: "العينات", risk: "تقييم المخاطر", review: "المراجعة" },
        field: {
          mat_pbt: "نسبة الأهمية النسبية — الربح قبل الضريبة",
          mat_revenue: "نسبة الأهمية النسبية — الإيرادات",
          mat_total_assets: "نسبة الأهمية النسبية — إجمالي الأصول",
          mat_equity: "نسبة الأهمية النسبية — حقوق الملكية",
          mat_expenses: "نسبة الأهمية النسبية — المصروفات",
          mat_other: "نسبة الأهمية النسبية — أساس آخر",
          pm_pct: "الأهمية النسبية للأداء % من الأهمية النسبية الإجمالية",
          ct_pct: "الحد الضئيل بوضوح % من الأهمية النسبية الإجمالية",
          sampling_min_confidence: "العينات — الحد الأدنى لمستوى الثقة %",
          expected_misstatement_pct: "العينات — التحريف المتوقع الافتراضي % من التحريف المقبول",
          significant_risk_criteria: "معايير المخاطر الجوهرية",
          review_levels: "مستويات المراجعة التي يطلبها المكتب",
        },
      },
      range: {
        firm: "نطاق المكتب {r}",
        firmMin: "الحد الأدنى للمكتب {r}",
        firmDefault: "القيمة الافتراضية للمكتب {r} من التحريف المقبول",
        version: "منهجية المكتب ⁦V{n}⁩",
        outside: "خارج نطاق المكتب — أدخل السبب (يُسجَّل انحراف)",
        below: "أقل من الحد الأدنى للمكتب — أدخل السبب (يُسجَّل انحراف)",
        reasonPh: "لماذا تستخدم هذه المهمة رقماً خارج منهجية المكتب",
        openDev: "انحراف مفتوح — السبب المسجل: {r}",
      },
      dev: {
        title: "انحرافات المنهجية",
        intro: "كل رقم حُفظ خارج إعدادات منهجية المكتب. يُقرّ شريك المهمة بكل انحراف مفتوح؛ وحتى ذلك الحين يُرفض توقيع الشريك وقفل الملف.",
        colItem: "المجال",
        colSetting: "الإعداد",
        colFirm: "إعداد المكتب",
        colValue: "قيمة المهمة",
        colReason: "السبب",
        colBy: "سجّله",
        colVersion: "الإصدار",
        colStatus: "الحالة",
        colAck: "الإقرار",
        status: { open: "مفتوح — غير مُقرّ به", acknowledged: "مفتوح — مُقرّ به", closed: "مُغلق" },
        area: { materiality: "الأهمية النسبية", sampling: "العينات" },
        ack: "إقرار",
        notePh: "ملاحظة (اختيارية)",
        acked: "أقرّ به {by} · {at}",
        closedAt: "أُغلق {at} (القيمة {v})",
        onlyPartner: "لا يُقرّ بالانحراف إلا شريك المهمة.",
        noPartner: "عيّن شريك المهمة في صفحة الفريق أولاً.",
        done: "تم الإقرار بالانحراف",
      },
      report: {
        title: "تقرير انحرافات المكتب",
        intro: "كل انحرافات المنهجية في مهام المكتب مع حالتها.",
        engagement: "المهمة",
        allEngagements: "كل المهام",
        area: "المجال",
        allAreas: "كل المجالات",
        status: "الحالة",
        allStatuses: "كل الحالات",
        export: "تصدير XLSX",
        empty: "لا توجد انحرافات.",
        counts: "{open} مفتوح — غير مُقرّ به · {acknowledged} مفتوح — مُقرّ به · {closed} مُغلق",
      },
    },
    errors: {
      METHODOLOGY_SETTINGS_FORBIDDEN: "لا يغيّر إعدادات المكتب لإصدار المنهجية إلا المدير (المدير الأول أو مدير المراجعة) أو الشريك / المدير التنفيذي أو مسؤول الشركة.",
      METHODOLOGY_SETTINGS_FROZEN: "هذا الإصدار من المنهجية مُستبدل: إعدادات المكتب الخاصة به مجمّدة (محفوظة كسجل).",
      METHODOLOGY_SETTING_VALUE_REQUIRED: "أدخل القيمة لتأكيدها — لا يوجد اقتراح لها في هذه المنهجية.",
      METHODOLOGY_SETTING_INVALID: "القيمة غير صالحة لهذا الإعداد.",
      METHODOLOGY_DEVIATION_REASON_REQUIRED: "أدخل سبب استخدام رقم خارج إعداد منهجية المكتب.",
      METHODOLOGY_DEVIATION_UNACKNOWLEDGED: "رُفض توقيع الشريك وقفل الملف: لم يُقرّ شريك المهمة بعد بانحرافات المنهجية.",
      METHODOLOGY_DEVIATION_PARTNER_ONLY: "لا يُقرّ بانحراف عن المنهجية إلا شريك المهمة.",
      METHODOLOGY_DEVIATION_CLOSED: "هذا الانحراف مُغلق: لا يوجد ما يُقرّ به.",
      METHODOLOGY_REPORT_FORBIDDEN: "لا يفتح تقرير انحرافات المكتب إلا المدير (المدير الأول أو مدير المراجعة) أو الشريك / المدير التنفيذي أو مسؤول الشركة.",
      METHODOLOGY_RELINK_LOCKED: "هذه المهمة موقّعة (مقفلة): لا يمكن تغيير إصدار المنهجية الخاص بها. افتح قفل المهمة أولاً.",
      METHODOLOGY_DELETE_CURRENT: "هذا هو إصدار المنهجية المعتمد الحالي: لا يمكن حذفه. اعتمد إصداراً أحدث أولاً، فيصبح هذا الإصدار مُستبدلاً.",
      METHODOLOGY_DELETE_LINKED: "تستخدم مهامٌ هذا الإصدار من المنهجية: لا يمكن حذفه. غيّر إصدار تلك المهام أولاً.",
    },
  },
};

export default I18N_S7E2;
